import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import { postData } from '../utility/Utility'
import { toast } from 'react-toastify'
import Chart from 'react-apexcharts'
import '../App.css'
function HomeComponent() {
    const [dashboardData, setDashboardData] = useState({
        totalUsers: 0,
        totalAgents: 0,
        totalPolicies: 0,
        totalEarnings: 0,
        totalPartners: 0,
        monthlyEarnings: []
    });
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterMonth, setFilterMonth] = useState('');
    const navigate = useNavigate();

    const fetchDashboardStats = async (filterStartDate = '', filterEndDate = '') => {
        try {
            setLoading(true);
            const response = await postData('api/admin/dashboard', {
                startDate: filterStartDate,
                endDate: filterEndDate
            });

            if (response?.success) {
                setDashboardData(response.data);
            } else {
                toast.error('Failed to fetch dashboard statistics');
            }
        } catch (error) {
            console.error('Dashboard stats error:', error);
            toast.error('Error loading dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const fetchMonthlyEarnings = async (filterYear = "", filterMonth = "") => {
        try {
            const response1 = await postData('api/admin/earnings/monthly', {
                year: filterYear || new Date().getFullYear(),
            });

            if (response1?.success) {
                setDashboardData(prev => ({
                    ...prev,
                    monthlyEarnings: response1.data
                }));
            }
        } catch (error) {
            console.error('Monthly earnings error:', error);
            toast.error('Failed to fetch monthly earnings data');
        }
    };

    const handleDateFilter = () => {
        if (startDate && endDate) {
            fetchDashboardStats(startDate, endDate);
        } else {
            toast.error('Please select both start date and end date');
        }
    };

    const handleResetFilter = () => {
        setStartDate('');
        setEndDate('');
        fetchDashboardStats();
    };

    useEffect(() => {
        fetchDashboardStats();
        fetchMonthlyEarnings();
    }, []);

    // Auto-trigger API when both dates are selected
    useEffect(() => {
        if (startDate && endDate) {
            fetchDashboardStats(startDate, endDate);
        }
    }, [startDate, endDate]);

    const formatNumber = (num) => {
        if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toString();
    };

    const dashboardCards = [
        {
            title: "Total Policies",
            value: dashboardData.totalPolicies,
            key: "totalPolicies",
            icon: "📋",
            gradient: "#1f5091",
            iconBg: "#667eea",
            direct: "/user-policy-list-by-agent"
        },
        {
            title: "Total Users",
            value: dashboardData.totalUsers,
            key: "totalUsers",
            icon: "👥",
            gradient: "#1f5091",
            iconBg: "#f093fb",
            direct: "/user-management"
        },
        {
            title: "Total Agents",
            value: dashboardData.totalAgents,
            key: "totalAgents",
            icon: "🏢",
            gradient: "#1f5091",
            iconBg: "#4facfe",
            direct: "/user-management"
        },
        {
            title: "Total Earnings",
            value: dashboardData.totalEarnings,
            key: "totalEarnings",
            icon: "💰",
            gradient: "#1f5091",
            iconBg: "#43e97b",
            direct: "/user-policy-list-by-agent"
        },
        {
            title: "Total Partners",
            value: dashboardData.totalPartners,
            key: "totalPartners",
            icon: "🤝",
            gradient: "#1f5091",
            iconBg: "#fa709a",
            direct: "/partner-list"
        }
    ];

    return (
        <>

            <div className="content-page">
                <div className="content">
                    <div className="container-fluid px-3 px-lg-4">
                        {/* Header Section */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
                                    <div>
                                        <h2 className="fw-bold text-dark mb-1">Dashboard Overview</h2>
                                    </div>

                                    {/* Date Filter Card */}
                                    <div className="card border-0 shadow-sm dashboard-filter-card">
                                        <div className="card-body p-3">
                                            <div className="row g-2 align-items-end">
                                                <div className="col-12 col-sm-4">
                                                    <label className="form-label small text-muted mb-1">Start Date</label>
                                                    <input
                                                        type="date"
                                                        id="startDate"
                                                        className="form-control form-control-sm border-0 bg-light dashboard-date-input"
                                                        value={startDate}
                                                        onChange={(e) => setStartDate(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-12 col-sm-4">
                                                    <label className="form-label small text-muted mb-1">End Date</label>
                                                    <input
                                                        type="date"
                                                        id="endDate"
                                                        className="form-control form-control-sm border-0 bg-light dashboard-date-input"
                                                        value={endDate}
                                                        onChange={(e) => setEndDate(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-12 col-sm-4">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm w-100 dashboard-reset-btn"
                                                        onClick={handleResetFilter}
                                                        disabled={loading}
                                                    >
                                                        🔄 Reset
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Dashboard Cards */}
                        <div className="row g-2 g-lg-3">
                            {dashboardCards.map((card, index) => (
                                <div key={card.key} className="col-6 col-md-4 col-lg-3 col-xl-2-4">
                                    <div
                                        className={`card border-0 shadow-sm position-relative overflow-hidden dashboard-card dashboard-card-${card.key}`}

                                        style={{ background: card.gradient }}>
                                        {/* Background Pattern */}
                                        <div className="dashboard-card-pattern"></div>

                                        <div className="card-body p-3 text-white position-relative d-flex flex-column justify-content-between h-100" onClick={() => navigate(card.direct)}>
                                            <div className="d-flex align-items-center">
                                                <div className="dashboard-card-icon"
                                                >
                                                    {card.icon}
                                                </div>
                                                <div className="flex-grow-1 text-center">
                                                    <h3 className="fw-bold mb-0 text-white dashboard-card-value">
                                                        {loading ? '...' : formatNumber(card.value)}
                                                    </h3>
                                                </div>
                                                {loading && (
                                                    <div className="spinner-border spinner-border-sm text-white" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="text-center">
                                                <p className="mb-0 text-white-50 fw-medium dashboard-card-title">
                                                    {card.title}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Charts Section */}
                        <div className="row mt-4">
                            {/* Users vs Agents Comparison */}
                            <div className="col-12 col-lg-6">
                                <div className="card border-0 shadow-sm dashboard-chart-card">
                                    <div className="card-header bg-transparent border-0 pb-0">
                                        <h5 className="fw-bold text-dark mb-0">Users vs Agents Comparison</h5>
                                        <p className="text-muted small mb-0">Real-time data comparison</p>
                                    </div>
                                    <div className="card-body">
                                        <Chart
                                            options={{
                                                chart: {
                                                    type: 'bar',
                                                    height: 300,
                                                    toolbar: {
                                                        show: false
                                                    },
                                                    background: 'transparent'
                                                },
                                                plotOptions: {
                                                    bar: {
                                                        horizontal: false,
                                                        columnWidth: '50%',
                                                        borderRadius: 8,
                                                        dataLabels: {
                                                            position: 'top'
                                                        }
                                                    }
                                                },
                                                dataLabels: {
                                                    enabled: true,
                                                    offsetY: -20,
                                                    style: {
                                                        fontSize: '14px',
                                                        fontWeight: 'bold',
                                                        colors: ['#333']
                                                    }
                                                },
                                                colors: ['#102c51', '#f36792'],
                                                xaxis: {
                                                    categories: ['Users', 'Agents'],
                                                    labels: {
                                                        style: {
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                            colors: ['#666']
                                                        }
                                                    }
                                                },
                                                yaxis: {
                                                    labels: {
                                                        style: {
                                                            fontSize: '12px',
                                                            colors: ['#666']
                                                        }
                                                    }
                                                },
                                                grid: {
                                                    borderColor: '#f1f1f1',
                                                    strokeDashArray: 3
                                                },
                                                tooltip: {
                                                    theme: 'light',
                                                    style: {
                                                        fontSize: '12px'
                                                    }
                                                }
                                            }}
                                            series={[{
                                                name: 'Count',
                                                data: [dashboardData.totalUsers, dashboardData.totalAgents]
                                            }]}
                                            type="bar"
                                            height={300}
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* monthly vs yearly earnings */}
                            <div className="col-12 col-lg-6">
                                <div className="card border-0 shadow-sm dashboard-chart-card">
                                    <div className="card-header bg-transparent border-0 pb-0 d-flex justify-content-between align-items-center">
                                        <h5 className="fw-bold text-dark mb-0">Monthly wise Earnings</h5>
                                        <div className="d-flex gap-2">
                                            <select
                                                className="form-select form-select-sm"
                                                value={filterYear}
                                                onChange={(e) => {
                                                    setFilterYear(e.target.value);
                                                    fetchMonthlyEarnings(e.target.value, filterMonth);
                                                }}
                                            >
                                                <option value="">All Years</option>
                                                {[...Array(61).keys()].map((_, i) => {
                                                    const year = new Date().getFullYear() - 10 + i;
                                                    return <option value={year} key={year}>{year}</option>;
                                                })}
                                            </select>

                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <Chart
                                            options={{
                                                chart: {
                                                    type: 'bar',
                                                    height: 300,
                                                    toolbar: {
                                                        show: false
                                                    },
                                                    background: 'transparent'
                                                },
                                                plotOptions: {
                                                    bar: {
                                                        horizontal: false,
                                                        columnWidth: '50%',
                                                        borderRadius: 8,
                                                        dataLabels: {
                                                            position: 'top'
                                                        }
                                                    }
                                                },
                                                dataLabels: {
                                                    enabled: true,
                                                    offsetY: -20,
                                                    style: {
                                                        fontSize: '14px',
                                                        fontWeight: 'bold',
                                                        colors: ['#333']
                                                    }
                                                },
                                                colors: ['#102c51', '#f36792'],
                                                xaxis: {
                                                    categories: Array(12).fill().map((_, i) => `${filterYear || new Date().getFullYear()}-${i + 1}`),
                                                    labels: {
                                                        style: {
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                            colors: ['#666']
                                                        }
                                                    }
                                                },
                                                yaxis: {
                                                    labels: {
                                                        style: {
                                                            fontSize: '12px',
                                                            colors: ['#666']
                                                        },
                                                        formatter: function (value) {
                                                            return '₹' + value.toLocaleString();
                                                        }
                                                    },
                                                    title: {
                                                        text: 'Earnings (₹)'
                                                    }
                                                },
                                                grid: {
                                                    borderColor: '#f1f1f1',
                                                    strokeDashArray: 3
                                                },
                                                tooltip: {
                                                    theme: 'light',
                                                    style: {
                                                        fontSize: '12px'
                                                    }
                                                }
                                            }}
                                            series={[{
                                                name: 'Earnings',
                                                data: Array(12).fill().map((_, i) => {
                                                    const monthData = dashboardData.monthlyEarnings?.find(
                                                        item => item.month.toLowerCase() ===
                                                            new Date(`${filterYear || new Date().getFullYear()}-${i + 1}-01`)
                                                                .toLocaleString('en-US', { month: 'long' }).toLowerCase()
                                                    );
                                                    return monthData ? monthData.total : 0;
                                                })
                                            }]}
                                            type="bar"
                                            height={300}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </>
    )
}

export default HomeComponent;